/*
 * Step2ReviewRules - Rules comparison and restore step
 *
 * UI/UX DESCRIPTION:
 * - Inline diff view showing old vs new rules
 * - Visual indicators for rule status (previous/new/restored)
 * - Restore buttons on old rules to preserve allocations
 * - Edit capability for restored rules
 *
 * FUNCTIONALITY:
 * - Compare AI-extracted rules with previous version
 * - Allow users to restore old rules to preserve data
 * - Show visual diff between rule sets
 * - Track which rules are restored vs new
 */

import React, { useState, useMemo } from 'react';
import {
  WarningCircleIcon,
  CircleNotchIcon,
  TrashIcon,
  ArrowCounterClockwiseIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import { Card, CardContent } from 'src/components/ui/card';
import { Badge } from 'src/components/ui/badge';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { RuleCardEditable } from 'src/components/rebates/agreement-detail/RuleCardEditable';
import { ExtractRulesOnlyResponse } from 'src/api/rebates/useCreateVersionWithExtraction';
import { useRebateRulesByAgreement } from 'src/api/rebates/useRebateRules';
import { DivisionSimpleDTO, RebateRule } from '@erp/shared-types';
import { ProductGroup as ApiProductGroup } from 'src/api/rebates/useProductGroups';
import { RuleWithVersionState } from './types';
import {
  wrapPreviousRule,
  toggleIncludeInNewVersion,
  updateRule,
  resetRuleToOriginal,
} from './utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@mui/material';

// Types
interface RebateAgreement {
  id: string;
  original_agreement_id: string;
  name: string;
  status: string;
}

interface RebateVersion {
  id: string;
  versionNumber: number;
  status: string;
}

interface Step2ReviewRulesProps {
  agreement: RebateAgreement;
  latestVersion: RebateVersion;
  wrappedRules: RuleWithVersionState[]; // Extracted rules already wrapped from Step1
  productGroups: ExtractRulesOnlyResponse['productGroups'];
  divisions: DivisionSimpleDTO[];
  onComplete: (
    updatedRules: RuleWithVersionState[],
    updatedProductGroups: ExtractRulesOnlyResponse['productGroups'],
  ) => void;
  onError: (error: string) => void;
  onBack: () => void;
  isLoading: boolean;
  error?: string;
}

const Step2ReviewRules: React.FC<Step2ReviewRulesProps> = ({
  agreement,
  latestVersion,
  wrappedRules,
  productGroups,
  divisions,
  onComplete,
  onError,
  onBack,
  isLoading,
  error,
}) => {
  // Fetch previous rules from the latest version
  const {
    data: previousRulesData,
    isLoading: previousRulesLoading,
    error: previousRulesError,
  } = useRebateRulesByAgreement(latestVersion.id);

  // Compute combined rules list at render time
  const baseCombinedRules = useMemo(() => {
    const alreadyContainsPrevious = wrappedRules.some(
      (r) => r.source === 'previous',
    );
    if (alreadyContainsPrevious) return wrappedRules;

    const wrappedPreviousRules = previousRulesData
      ? previousRulesData.map(wrapPreviousRule)
      : [];

    return [...wrappedRules, ...wrappedPreviousRules];
  }, [previousRulesData, wrappedRules]);

  // State for tracking user modifications to rules
  const [allRules, setAllRules] =
    useState<RuleWithVersionState[]>(baseCombinedRules);
  const [productGroupsState, setProductGroupsState] = useState(productGroups);
  const [prevBaseCombinedRules, setPrevBaseCombinedRules] =
    useState(baseCombinedRules);

  const [showAddProductGroupModal, setShowAddProductGroupModal] =
    useState(false);

  // Sync state when base combined rules change (render-time state adjustment)
  if (baseCombinedRules !== prevBaseCombinedRules) {
    setPrevBaseCombinedRules(baseCombinedRules);
    setAllRules(baseCombinedRules);
  }

  // 1️⃣ Compute which product groups are actually used
  const usedProductGroupIds = new Set<string>();

  allRules
    .filter((r) => r.includeInNewVersion)
    .forEach((r) => {
      r.rule.qualifyingProductGroups?.forEach((pg) =>
        usedProductGroupIds.add(pg.id),
      );
      r.rule.affectedProductGroups?.forEach((pg) =>
        usedProductGroupIds.add(pg.id),
      );
    });

  // 2️⃣ Merge all + new product groups safely (no duplicates) and flag unused ones
  const mergedGroups = [
    ...productGroupsState.all,
    ...productGroupsState.new.filter(
      (pg) => !productGroupsState.all.some((g) => g.id === pg.id),
    ),
  ];

  const previewProductGroups = mergedGroups.map((pg) => ({
    ...pg,
    isDeleted: !usedProductGroupIds.has(pg.id),
  }));

  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());

  // Handler: Toggle whether a rule should be included in the new version
  const handleToggleRule = (ruleId: string) => {
    setAllRules((prev) =>
      prev.map((wrapped) =>
        wrapped.rule.id === ruleId
          ? toggleIncludeInNewVersion(wrapped)
          : wrapped,
      ),
    );
  };

  // Handler: Toggle rule expansion in UI
  const handleToggleExpansion = (ruleId: string) => {
    setExpandedRules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(ruleId)) {
        newSet.delete(ruleId);
      } else {
        newSet.add(ruleId);
      }
      return newSet;
    });
  };

  // Handler: Update rule and mark as edited
  const handleRuleUpdate = (ruleId: string, updatedRule: RebateRule) => {
    setAllRules((prev) =>
      prev.map((wrapped) =>
        wrapped.rule.id === ruleId ? updateRule(wrapped, updatedRule) : wrapped,
      ),
    );
  };

  // Handler: Reset rule to original state
  const handleResetRule = (ruleId: string) => {
    setAllRules((prev) =>
      prev.map((wrapped) =>
        wrapped.rule.id === ruleId ? resetRuleToOriginal(wrapped) : wrapped,
      ),
    );
  };

  // Convert extraction API ProductGroup to API ProductGroup for RuleCardEditable
  const convertToApiProductGroups = (
    groups: ExtractRulesOnlyResponse['productGroups'],
  ): ApiProductGroup[] => {
    // Add null/undefined check to prevent runtime errors
    if (!groups || !groups.all) {
      return [];
    }

    // Combine 'all' (existing) and 'new' (extracted) product groups
    // Remove duplicates by using a Map with id as key
    const allGroups = [...groups.all, ...(groups.new || [])];
    const uniqueGroups = new Map();

    allGroups.forEach((group) => {
      uniqueGroups.set(group.id, {
        id: group.id,
        name: group.name,
        description: group.description || null,
        supplierId: group.supplierId,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    });

    return Array.from(uniqueGroups.values());
  };
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');

  // Handler: Proceed to next step
  const handleProceed = () => {
    onComplete(allRules, productGroupsState);
  };

  // Validation: can proceed if we have extracted rules available
  const extractedRulesCount = allRules.filter(
    (r) => r.source === 'extracted',
  ).length;
  const canProceed =
    extractedRulesCount > 0 && !isLoading && !previousRulesLoading;

  // Get status badge info for a rule
  const getRuleStatus = (wrapped: RuleWithVersionState) => {
    if (wrapped.source === 'manual') return 'manual';
    if (!wrapped.includeInNewVersion) return 'deleted';
    if (wrapped.source === 'previous') {
      return wrapped.hasBeenEdited ? 'modified' : 'restored';
    } else {
      return wrapped.hasBeenEdited ? 'modified' : 'new';
    }
  };

  const handleAddRule = () => {
    const ruleDefaults: RebateRule = {
      id: `temp-${crypto.randomUUID()}`,
      name: 'New Rule',
      isManual: false,
      thresholdType: 'volume',
      qualifyingProductGroups: [],
      affectedProductGroups: [],
      qualifyingInclusionType: 'exclusive',
      affectedInclusionType: 'exclusive',
      growthBaseline: null,
      discountPercentage: '0',
      notes: null,
      sortOrder: allRules.length,
      isRetroactiveToDollarOne: false,
      tiers: [],
      divisions: [],
      startDate: null,
      endDate: null,
      createdAt: new Date(),
      updatedAt: new Date(),
      rebateAgreementId: agreement.id,
    };
    const newRule: RuleWithVersionState = {
      originalRule: { ...ruleDefaults },
      rule: { ...ruleDefaults },
      source: 'manual',
      includeInNewVersion: true,
      hasBeenEdited: true,
    };

    setAllRules((prev) => [...prev, newRule]);
    setExpandedRules((prev) => new Set([...prev, newRule.rule.id]));
  };
  // Filter rules by source for display
  const extractedRules = allRules.filter(
    (r) => r.source === 'extracted' || r.source === 'manual',
  );
  const previousRules = allRules.filter((r) => r.source === 'previous');

  const convertedProductGroups = convertToApiProductGroups(productGroupsState);

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-xl font-semibold mb-2">Review Extracted Rules</h2>
          <p className="text-muted-foreground">
            By default, all previous rules are replaced with the new
            AI-extracted rules. You can restore specific old rules to preserve
            existing allocations and manually recorded rebates.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-2 pb-4 border-b">
          <Button onClick={onBack} variant="outline" disabled={isLoading}>
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handleProceed}
            disabled={!canProceed}
            className="gap-2"
          >
            Continue to Impact Preview
            <ArrowRightIcon className="h-4 w-4 ml-2" />
          </Button>
        </div>

        {/* Error Display */}
        {(error || previousRulesError) && (
          <AlertContainer variant="destructive">
            <WarningCircleIcon className="h-4 w-4" />
            <AlertDescription>
              {error || 'Failed to load previous rules'}
            </AlertDescription>
          </AlertContainer>
        )}

        {/* Loading State */}
        {previousRulesLoading && (
          <div className="flex items-center justify-center py-8">
            <CircleNotchIcon className="h-6 w-6 animate-spin mr-2" />
            <span className="text-muted-foreground">
              Loading previous rules...
            </span>
          </div>
        )}

        {/* Rules Comparison */}
        {!previousRulesLoading && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Rules Comparison</h3>
              <Button size="sm" onClick={handleAddRule}>
                + Add New Rule
              </Button>
            </div>

            {/* New Extracted Rules */}
            <div className="space-y-3">
              <h4 className="text-md font-medium text-blue-700">
                New Extracted Rules
              </h4>
              {extractedRules.length > 0 ? (
                extractedRules.map((wrapped) => {
                  const status = getRuleStatus(wrapped);
                  const isExpanded = expandedRules.has(wrapped.rule.id);

                  return (
                    <RuleCardEditable
                      key={wrapped.rule.id}
                      rule={wrapped.rule}
                      isExpanded={isExpanded}
                      onToggleExpansion={() =>
                        handleToggleExpansion(wrapped.rule.id)
                      }
                      onUpdate={(ruleId, updatedRule) =>
                        handleRuleUpdate(ruleId, updatedRule)
                      }
                      onValidationChange={() => {}}
                      onDelete={() => handleToggleRule(wrapped.rule.id)}
                      divisions={divisions}
                      productGroups={convertedProductGroups}
                      agreementStatus="draft"
                      hasErrors={false}
                      validationErrors={[]}
                      changeStatus={status}
                      deleteIcon={
                        wrapped.includeInNewVersion
                          ? TrashIcon
                          : ArrowCounterClockwiseIcon
                      }
                      deleteTooltip={
                        wrapped.includeInNewVersion
                          ? 'Exclude this rule'
                          : 'Include this rule'
                      }
                    />
                  );
                })
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No new extracted rules available</p>
                </div>
              )}
            </div>

            {/* Previous Version Rules */}
            <div className="space-y-3">
              <h4 className="text-md font-medium text-gray-700">
                Previous Version Rules
              </h4>
              {previousRules.length > 0 ? (
                previousRules.map((wrapped) => {
                  const status = getRuleStatus(wrapped);
                  const isExpanded = expandedRules.has(wrapped.rule.id);

                  return (
                    <RuleCardEditable
                      key={wrapped.rule.id}
                      rule={wrapped.rule}
                      isExpanded={isExpanded}
                      onToggleExpansion={() =>
                        handleToggleExpansion(wrapped.rule.id)
                      }
                      onUpdate={(ruleId, updatedRule) =>
                        handleRuleUpdate(ruleId, updatedRule)
                      }
                      onValidationChange={() => {}}
                      onDelete={() => handleToggleRule(wrapped.rule.id)}
                      divisions={divisions}
                      productGroups={convertedProductGroups}
                      agreementStatus="draft"
                      hasErrors={false}
                      validationErrors={[]}
                      changeStatus={status}
                      deleteIcon={
                        wrapped.includeInNewVersion
                          ? TrashIcon
                          : ArrowCounterClockwiseIcon
                      }
                      deleteTooltip={
                        wrapped.includeInNewVersion
                          ? 'Exclude from new version'
                          : 'Restore to new version'
                      }
                    />
                  );
                })
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No previous rules found</p>
                </div>
              )}
            </div>

            {/* Product Groups Preview */}
            <div className="space-y-3">
              <h4 className="text-md font-medium text-purple-700">
                Product Groups Preview
              </h4>
              <p className="text-sm text-muted-foreground">
                Available product groups for rule configuration (existing +
                newly extracted). Unused new product groups will be marked as
                deleted.
              </p>
              <Button
                size="sm"
                onClick={() => setShowAddProductGroupModal(true)}
              >
                + Add Product Group
              </Button>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {previewProductGroups.length > 0 ? (
                  previewProductGroups.map((group) => (
                    <Card
                      key={group.id}
                      className={`border ${
                        group.isDeleted
                          ? 'border-red-300 bg-red-50 opacity-70'
                          : group.isNew
                            ? 'border-purple-300 bg-purple-50'
                            : 'border-gray-300 bg-gray-50'
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-3 h-3 rounded-full ${
                                group.isDeleted
                                  ? 'bg-red-500'
                                  : group.isNew
                                    ? 'bg-purple-500'
                                    : 'bg-gray-400'
                              }`}
                            />
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h5
                                  className={`font-medium ${
                                    group.isDeleted
                                      ? 'line-through text-red-700'
                                      : ''
                                  }`}
                                >
                                  {group.name}
                                </h5>
                                <Badge
                                  variant="outline"
                                  className={
                                    group.isDeleted
                                      ? 'bg-red-100 text-red-700'
                                      : group.isNew
                                        ? 'bg-purple-100 text-purple-700'
                                        : 'bg-gray-100 text-gray-700'
                                  }
                                >
                                  {group.isDeleted
                                    ? 'Unused'
                                    : group.isNew
                                      ? 'New'
                                      : 'Existing'}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No product groups available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Summary */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <h4 className="font-medium">Summary</h4>
                <p className="text-sm font-medium">
                  {allRules.filter((r) => r.includeInNewVersion).length} active
                  rules
                </p>
                <p className="text-xs text-muted-foreground">
                  {
                    allRules.filter(
                      (r) => r.source === 'extracted' && r.includeInNewVersion,
                    ).length
                  }{' '}
                  new +{' '}
                  {
                    allRules.filter(
                      (r) => r.source === 'previous' && r.includeInNewVersion,
                    ).length
                  }{' '}
                  restored
                  {allRules.filter((r) => r.hasBeenEdited).length > 0 &&
                    ` + ${allRules.filter((r) => r.hasBeenEdited).length} edited`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {showAddProductGroupModal && (
        <Dialog
          open={showAddProductGroupModal}
          onOpenChange={setShowAddProductGroupModal}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Product Group</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <Input
                placeholder="Group Name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
              />
              <Textarea
                placeholder="Description (optional)"
                value={newGroupDescription}
                onChange={(e) => setNewGroupDescription(e.target.value)}
              />
            </div>

            <DialogFooter className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowAddProductGroupModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const newGroup = {
                    id: `temp-${crypto.randomUUID()}`,
                    name: newGroupName.trim() || 'Untitled Group',
                    description: newGroupDescription.trim(),
                    isNew: true,
                    supplierId: agreement.id,
                    isDeleted: false,
                  };
                  setProductGroupsState((prev) => ({
                    ...prev,
                    new: [...prev.new, newGroup],
                    all: prev.all.some((g) => g.id === newGroup.id)
                      ? prev.all
                      : [...prev.all, newGroup],
                  }));
                  setNewGroupName('');
                  setNewGroupDescription('');
                  setShowAddProductGroupModal(false);
                }}
              >
                Add Group
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default Step2ReviewRules;
